/**
 * API istemcisi (PDF §12, §13). PWA aynı kökenden çalışır:
 *  - Oturum HttpOnly + Secure çerezde; JavaScript belirtece erişemez.
 *  - Yazma isteklerinde CSRF başlığı (aksan_csrf çerezinden) ve Idempotency-Key.
 *  - Kesinleştirme gibi kritik çağrılarda anahtar taslağa yazılır; yeniden denemede aynı anahtar gider (T08).
 */
import { ApiError, NetworkError } from '../lib/errors';
import { uuid } from '../lib/uuid';

let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

function csrfFromCookie(): string {
  const m = /(?:^|;\s*)aksan_csrf=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1] ?? '') : '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | undefined | null>;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const url = new URL(`/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') {
    headers['Idempotency-Key'] = opts.idempotencyKey ?? uuid();
    headers['X-CSRFToken'] = csrfFromCookie();
  }
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'same-origin', cache: 'no-store', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new NetworkError();
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const err = (data ?? {}) as { code?: string; message?: string; details?: Record<string, unknown> };
    const apiErr = new ApiError(err.code ?? `HTTP_${res.status}`, err.message ?? fallbackMessage(res.status), res.status, err.details ?? {});
    if (['UNAUTHENTICATED', 'SESSION_REVOKED', 'MEMBERSHIP_INACTIVE'].includes(apiErr.code)) onUnauthenticated?.();
    throw apiErr;
  }
  return data as T;
}

/** Sunucu JSON hata gövdesi döndürmediğinde (vekil sunucu hatası, zaman aşımı) kullanıcıya anlaşılır mesaj. */
function fallbackMessage(status: number): string {
  if (status === 413) return 'Dosya çok büyük. Lütfen fotoğrafı yeniden çekin.';
  if (status === 502 || status === 503 || status === 504) {
    return 'Sunucuya şu an ulaşılamadı veya yükleme zaman aşımına uğradı. Bilgileriniz cihazda saklandı; "Tekrar dene" ile yeniden gönderin.';
  }
  return 'İstek başarısız.';
}
